import React, { useState, useEffect, useRef } from "react";
import DropDown from "../../../common/components/dropdown";
import history from "../../../managers/history";
import moment from "moment/moment";
import SelectDateRange from "../../../common/components/selectDateRange";
import { useDispatch, useSelector } from "react-redux";
import Utils from "../../../utility";
import { UserConsent } from "../../../services";
import {
  failureMessageConstant,
  uiConstant,
  keyConstants
} from "../../../constants";
const { QUERY_FAILED } = failureMessageConstant;
import RewardPopup from "../../dialog/rewardPopup";
import CustomPagination from "../../../common/components/customPagination";
import { removeUser } from "../../../slices/userSlice";
import PropTypes from "prop-types";

const ConsentListComponent = (props) => {
  const dispatch = useDispatch();
  const { states, setState, handleChangePage } = props;
  const { userConsentData, page, totalItems, itemsPerPage } = states;
  const user = useSelector((state) => state.user);
  const [searchString, setSearchString] = useState("");
  const [dates, setDates] = useState({ start: null, end: null });
  const [calenderOpen, setCalenderOpen] = useState(false);
  const [showOption, setShowOption] = useState(false);
  const [consentType, setConsentType] = useState({ _id: "", name: "" });
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isApplyCount, setIsApplyCount] = useState(0);
  const consentData = uiConstant.setupConsent.consentData;
  const calendarDropDownRef = useRef(null);

  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;

  const fetchConsentList = async (skip, limit, isDatesCleared) => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(fetchConsentList);
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    {
      // Prepare the data for the API request
      const data = {
        businessId: user?._id,
        search: searchString || "",
        consentDateFrom: (!isDatesCleared && dates?.start) || null,
        consentDateTo: (!isDatesCleared && dates?.end) || null,
        consentType:
          consentType?.name === "All Consents"
            ? null
            : consentType?.name || null,
        skip: skip,
        limit: limit
      };

      // Make the API request and parse the response
      const [error, searchData] = await Utils.parseResponse(
        new UserConsent().getConsentById(
          data?.businessId,
          data?.skip,
          data?.limit,
          {
            consentType: data?.consentType,
            searchQuery: data?.search,
            startTime: data?.consentDateFrom,
            endTime: data?.consentDateTo
          }
        )
      );

      // Handle any errors that occurred during the API request
      if (error) {
        Utils.failureToastMessage(QUERY_FAILED);
        return;
      }

      // update the state with the search data
      setState("userConsentData", searchData?.data);
      setState("totalItems", searchData?.total);
    }
  };

  useEffect(() => {
    fetchConsentList(0, itemsPerPage, false);
  }, [searchString, consentType]);
  useEffect(() => {
    fetchConsentList((page - 1) * itemsPerPage, itemsPerPage, false);
  }, [page, isFilterApplied]);

  useEffect(() => {
    if (isApplyCount > 1) {
      fetchConsentList((page - 1) * itemsPerPage, itemsPerPage, false);
    }
  }, [isApplyCount]);

  const navigateToConsentDetail = (consentDetail) => {
    history.push(`/consent/consent-detail/${consentDetail._id}`);
  };

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    setShowPopup(!user?.rewardId); // Update state based on user's rewardId
  }, [user?.rewardId]); // Trigger effect whenever user's rewardId changes

  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (
        calendarDropDownRef.current &&
        !calendarDropDownRef.current.contains(event.target)
      ) {
        setCalenderOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, []);

  const togglePopup = () => {
    setShowPopup(!showPopup);
  };

  const getStatus = (element) => {
    return (
      element.consents.length > 0 &&
      element.consents.every((consent) => consent.isRevoked)
    );
  };

  return (
    <div className="mb-20">
      <div className="mt-5 size-full px-4 md:pl-12.5 md:pr-29px">
        <p className="font-PoppinsSemiBold text-ft22-33-0.4 text-black-20">
          Provided Consents
        </p>
        <div className="mt-4 flex flex-col items-center justify-between gap-2 md:mt-5 md:flex-row">
          <div className="flex w-full flex-col flex-wrap items-center gap-15px md:flex-row lg:flex-nowrap ">
            <div className="flex h-10 w-full items-center gap-3 rounded border border-grey-D9D9D9 py-7px pl-3 md:max-w-241px">
              <img src="/images/search-icon.svg" alt="search"></img>
              <input
                type="text"
                placeholder="Search"
                className="size-full border-none font-MontserratMedium text-ft15-26.25 text-black-000000 outline-none placeholder:text-black-000000"
                onChange={(e) => {
                  setSearchString(e.target.value);
                }}
              ></input>
            </div>
            <SelectDateRange
              dates={dates}
              setDates={setDates}
              placeHolder={"Consent Date"}
              calenderOpen={calenderOpen}
              setCalenderOpen={setCalenderOpen}
              calendarDropDownRef={calendarDropDownRef}
              id="providedConsent"
              setPage={setState}
              setIsFilterApplied={setIsFilterApplied}
              setIsApplyCount={setIsApplyCount}
              isApplyCount={isApplyCount}
            />
            <DropDown
              value={consentType}
              setValue={setConsentType}
              data={consentData}
              placeHolder={"Consent Type"}
              customizeDropDown={{
                maxWidth: "md:max-w-241px",
                contentHeight: "h-10"
              }}
              showOption={showOption}
              setShowOption={setShowOption}
            />
          </div>
        </div>
        {userConsentData.length !== 0 ? (
          <div className="relative mt-8 overflow-auto rounded border border-grey-D9D9D9">
            <table className="w-full text-left">
              <thead className="border-none border-b-grey-D9D9D9">
                <tr>
                  <th
                    scope="col"
                    className="whitespace-nowrap pb-2.5 pl-27px pt-3 font-MontserratSemiBold text-ft16-19.5"
                  >
                    Public ID
                  </th>
                  <th
                    scope="col"
                    className="whitespace-nowrap pb-2.5 pl-27px pt-3 font-MontserratSemiBold text-ft16-19.5"
                  >
                    Last Updated
                  </th>
                  <th
                    scope="col"
                    className="whitespace-nowrap pb-2.5 pl-27px pt-3 font-MontserratSemiBold text-ft16-19.5"
                  >
                    Joined on
                  </th>
                  <th
                    scope="col"
                    className="whitespace-nowrap pb-2.5 pl-27px pt-3 font-MontserratSemiBold text-ft16-19.5"
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {userConsentData.map((element) => (
                  <tr
                    key={element?._id}
                    onClick={() => navigateToConsentDetail(element)}
                    className="cursor-pointer border-t border-grey-D9D9D9 bg-white"
                  >
                    <td className="cursor-pointer whitespace-nowrap py-11px pl-27px font-PoppinsRegular text-ft15-26.25">
                      {Utils.truncateWalletAddress(element?.walletAddress)}
                    </td>
                    <td className="cursor-pointer whitespace-nowrap py-11px pl-27px font-PoppinsRegular text-ft15-26.25">
                      {moment(element.updatedOn)
                        .utcOffset("+05:30")
                        .format("hh:mm A, DD MMM YYYY")}
                    </td>
                    <td className="cursor-pointer whitespace-nowrap py-11px pl-27px font-PoppinsRegular text-ft15-26.25">
                      {moment(element?.addedOn)
                        .utcOffset("+05:30")
                        .format("hh:mm A, DD MMM YYYY")}
                    </td>
                    <td className="cursor-pointer whitespace-nowrap py-11px pl-27px font-PoppinsRegular text-ft15-26.25">
                      {getStatus(element) ? "Inactive" : "Active"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{ height: window.innerHeight - 230 }} // Managing height here using window.innerHeight
            className="mt-8 grid w-full place-items-center rounded border border-solid border-grey-D9D9D9"
          >
            <div className="flex flex-col items-center gap-9">
              <img src="/images/arrow-image.svg" alt="No consent" />
              <div className="font-PoppinsMedium text-ft22-33-0.4 text-grey-767676">
                No Consent Provided Yet
              </div>
            </div>
          </div>
        )}
        {totalItems > itemsPerPage && (
          <CustomPagination
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            currentPage={page}
            handleChangePage={handleChangePage}
          />
        )}
        {user?.rewardId == undefined
          ? ""
          : !user?.rewardId &&
            showPopup && <RewardPopup handleClose={togglePopup} />}
      </div>
    </div>
  );
};
ConsentListComponent.propTypes = {
  states: PropTypes.shape({
    userConsentData: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      })
    ),
    page: PropTypes.number.isRequired,
    totalItems: PropTypes.number.isRequired,
    itemsPerPage: PropTypes.number.isRequired
  }).isRequired,
  setState: PropTypes.func.isRequired,
  handleChangePage: PropTypes.func.isRequired
};

export default ConsentListComponent;
